import { createContext, ReactNode, useEffect, useState } from "react";
import { ColumnDefaultsResponse } from "../types";
import { useApi } from "../hooks/useApi";

// DB-driven ag-grid column display defaults (gui.column_default), fetched once per login and
// merged into every grid's defaultColDef/columnDefs via buildDefaultColDef
// (util/AgGridFunctions.ts). Deliberately NOT folded into CurrentUserContext: that context's
// fetches are genuine access gates (failure alerts + logs the user out, see its own comments) --
// this is a display nicety, so a failed/slow fetch must never block login or the app rendering,
// it should just leave grids using only their existing code-level defaults.
const EMPTY_COLUMN_DEFAULTS: ColumnDefaultsResponse = {};

export const ColumnDefaultsContext = createContext<ColumnDefaultsResponse>(EMPTY_COLUMN_DEFAULTS);

interface ColumnDefaultsProviderProps {
    children: ReactNode;
}

export function ColumnDefaultsProvider({ children }: ColumnDefaultsProviderProps) {
    const { getColumnDefaults, ready: apiReady } = useApi();
    const [defaults, setDefaults] = useState<ColumnDefaultsResponse>(EMPTY_COLUMN_DEFAULTS);

    useEffect(() => {
        if (!apiReady) return;
        getColumnDefaults()
            .then(setDefaults)
            .catch((err) => {
                console.error("Failed to load column defaults:", err);
            });
    }, [apiReady]);

    return (
        <ColumnDefaultsContext.Provider value={defaults}>
            {children}
        </ColumnDefaultsContext.Provider>
    );
}
